import axios from "axios";

const API_BASE = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_URL || "");

const client = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
  if (token) {
    localStorage.setItem("ds_token", token);
  } else {
    localStorage.removeItem("ds_token");
  }
}

export function getAccessToken() {
  if (!accessToken) {
    accessToken = localStorage.getItem("ds_token");
  }
  return accessToken;
}

function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf-token=([^;]+)/);
  return match ? match[1] : null;
}

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.method && ["post", "put", "patch", "delete"].includes(config.method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }

  return config;
});

client.interceptors.response.use(
  (res) => {
    const csrfHeader = res.headers["x-csrf-token"];
    if (csrfHeader) {
      document.cookie = `csrf-token=${csrfHeader}; path=/; SameSite=Strict`;
    }
    return res;
  },
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = (async () => {
            const res = await axios.post(
              `${API_BASE}/api/auth/refresh`,
              {},
              { withCredentials: true }
            );
            const newToken = res.data?.data?.accessToken;
            if (newToken) {
              setAccessToken(newToken);
            }
            return newToken;
          })();
        }
        const newToken = await refreshPromise;
        refreshPromise = null;
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return client(original);
        } else {
          setAccessToken(null);
          window.location.href = "/login";
        }
      } catch {
        refreshPromise = null;
        setAccessToken(null);
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default client;
